import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

const SpiderChart = ({ 
    radarData = [], 
    facilities = [], // 新增 facilities 参数来获取颜色信息
    title = "Portfolio Performance Overview", 
    style = { width: '100%', height: '400px' } 
}) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);

    // 初始化雷达图
    const initRadarChart = (retryCount = 0) => {
        const chartDom = chartRef.current;
        if (!chartDom) {
            if (retryCount < 5) {
                setTimeout(() => initRadarChart(retryCount + 1), 500);
            }
            return;
        }

        if (!radarData.length) {
            console.log('No radar data');
            return;
        }
        
        // 检查容器尺寸
        if (chartDom.offsetWidth === 0 || chartDom.offsetHeight === 0) {
            setTimeout(() => initRadarChart(), 500);
            return;
        }
        
        // 清除之前的图表实例
        if (chartInstanceRef.current) {
            chartInstanceRef.current.dispose();
        }
        
        const myChart = echarts.init(chartDom);
        chartInstanceRef.current = myChart;
        
        // 创建 facility 颜色映射
        const facilityColorMap = {};
        facilities.forEach(facility => {
            facilityColorMap[facility.id] = facility.color;
        });
        
        const option = {
            backgroundColor: '#fff',
            tooltip: {
                trigger: 'item',
                confine: false, // 允许tooltip超出canvas边界
                enterable: true, // 允许鼠标进入tooltip
                appendToBody: true, // 将tooltip添加到body，避免被容器限制
                extraCssText: 'z-index: 9999;', // 设置更高的z-index
                className: 'custom-tooltip',
                formatter: function(params) {
                    const { name, value } = params;
                    let result = `<strong>${name}</strong><br/>`;
                    
                    const indicators = [
                        'Energy Efficiency',
                        'Equipment Condition',
                        'Occupant Comfort',
                        'Management Efficiency',
                        'Technology Adoption',
                        'Cost Efficiency'
                    ];
                    
                    indicators.forEach((indicator, index) => {
                        if (value[index] !== undefined) {
                            result += `${indicator}: ${value[index]}<br/>`;
                        }
                    });
                    
                    return result;
                }
            },
            legend: {
                show: false
            },
            radar: {
                indicator: [
                    { name: 'Energy Efficiency', max: 10 },
                    { name: 'Equipment Condition', max: 10 },
                    { name: 'Occupant Comfort', max: 10 },
                    { name: 'Management Efficiency', max: 10 },
                    { name: 'Technology Adoption', max: 10 },
                    { name: 'Cost Efficiency', max: 10 }
                ],
                center: ['50%', '50%'],
                radius: '60%',
                axisName: {
                    color: '#666',
                    fontSize: 12
                },
                splitLine: {
                    lineStyle: {
                        color: '#e6e6e6'
                    }
                },
                axisLine: {
                    lineStyle: {
                        color: '#e6e6e6'
                    }
                }
            },
            series: [{
                type: 'radar',
                data: radarData.map((item, index) => ({
                    value: item.radarData,
                    name: item.facilityName,
                    itemStyle: {
                        // 使用 facility 的颜色，如果没有则使用默认颜色
                        color: facilityColorMap[item.facilityId] || `hsl(${index * 360 / radarData.length}, 70%, 50%)`
                    },
                    lineStyle: {
                        color: facilityColorMap[item.facilityId] || `hsl(${index * 360 / radarData.length}, 70%, 50%)`
                    },
                    areaStyle: {
                        opacity: 0 // 设置为完全透明，不显示填充
                    }
                }))
            }]
        };
        
        myChart.setOption(option);
        
        console.log('Radar chart option set successfully');
        
        // 响应式处理
        const resizeHandler = () => {
            if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
                chartInstanceRef.current.resize();
            }
        };
        
        window.addEventListener('resize', resizeHandler);
        
        // 强制重绘
        setTimeout(() => {
            if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
                chartInstanceRef.current.resize();
            }
        }, 100);
        
        // 清理函数
        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    };

    useEffect(() => {
        // 使用 requestAnimationFrame 确保DOM完全渲染
        const timer = setTimeout(() => {
            requestAnimationFrame(() => {
                initRadarChart();
            });
        }, 100);

        return () => {
            clearTimeout(timer);
            if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
                chartInstanceRef.current.dispose();
            }
        };
    }, [radarData, facilities, title]);

    return (
        <div 
            ref={chartRef}
            style={style}
        />
    );
};

export default SpiderChart;

